import {reaction} from 'mobx';
import {observer} from 'mobx-react-lite';
import type {ComponentType, Ref} from 'react';
import React, {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
} from 'react';
import type {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollViewProps,
  ViewStyle,
} from 'react-native';
import {Platform, ScrollView, StyleSheet, View} from 'react-native';

import {lightImpact} from '../../haptics';
import {useObservableRef} from '../../mobx-react-toolbox';
import type {OptionalGetter} from '../../mobx-toolbox';
import {use} from '../../mobx-toolbox';
import getVisibleIndex from './getVisibleIndex';
import useGetContentWidth from './useGetContentWidth';
import useGetOffset from './useGetOffset';
import useScrollEndEffect from './useScrollEndEffect';

export type CarouselScrollViewProps<T extends object> = {
  slides: readonly SlideMeta<T>[];
  SlideComponent: ComponentType<T>;
  getIndex: () => number;
  setIndex: (index: number) => void;
  getIsFocused?: OptionalGetter<boolean> | undefined;
  scrollViewRef?: Ref<ScrollView | null>;
} & ScrollViewProps;

export type SlideMeta<T extends object> = {
  key: string;
  props: T;
};

export default observer(function CarouselScrollView<T extends object>(
  props: CarouselScrollViewProps<T>,
) {
  const {
    slides,
    SlideComponent,
    getIndex,
    setIndex,
    getIsFocused,
    scrollViewRef,
    ...rest
  } = props;

  const slideCount = slides.length;

  const scrollRef = useObservableRef<ScrollView>(null);
  useImperativeHandle(scrollViewRef, () => scrollRef.current, [scrollRef]);

  const [getOffset, setOffset] = useGetOffset(scrollRef);
  const [getContentWidth, onContentSizeChange] = useGetContentWidth(scrollRef);

  const processFinalOffset = useCallback(
    (offset: number) => {
      setOffset?.(offset);
      const width = getContentWidth();
      if (width === undefined) {
        return;
      }
      const index = getVisibleIndex(offset, width, slideCount);
      const isExactlyAtSnappingPoint = Number.isInteger(index);
      if (isExactlyAtSnappingPoint) {
        setIndex(index);
        if (Platform.OS === 'ios') {
          lightImpactIfPossible();
        }
      }
    },
    [setOffset, getContentWidth, slideCount, setIndex],
  );

  const onScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      processFinalOffset(event.nativeEvent.contentOffset.x);
    },
    [processFinalOffset],
  );

  const onMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (Platform.OS === 'ios' && use(getIsFocused) === false) {
        // avoid iOS bug
        return;
      }
      onScrollEnd(event);
    },
    [getIsFocused, onScrollEnd],
  );

  const contentContainerStyle = useMemo<ViewStyle>(
    () => ({width: `${slideCount * 100}%`}),
    [slideCount],
  );

  useScrollEndEffect(scrollRef, processFinalOffset);

  useLayoutEffect(
    () =>
      reaction(
        () => [scrollRef.current, getContentWidth(), getIndex()] as const,
        (current, previous) => {
          const [scroll, width, index] = current;
          // the width can be zero on web when the screen loses focus
          if (scroll === null || width === undefined || width === 0) {
            return;
          }

          const offset = getOffset() ?? 0;
          const visibleIndex = getVisibleIndex(offset, width, slideCount);
          if (index !== visibleIndex) {
            const x = (index * width) / slideCount;
            const [_scroll, _width] = previous ?? [];
            const layoutRemainsTheSame =
              Object.is(scroll, _scroll) && Object.is(width, _width);
            scroll.scrollTo({x, animated: layoutRemainsTheSame});
            setOffset?.(x);
          }
        },
        {name: 'TabView#scroll', fireImmediately: true},
      ),
    [getContentWidth, getIndex, getOffset, scrollRef, setOffset, slideCount],
  );

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      pagingEnabled={Platform.OS !== 'web'}
      dataSet={carouselDataSet}
      contentContainerStyle={[
        layoutStyles.contentContainerStyle,
        contentContainerStyle,
      ]}
      disableIntervalMomentum
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      decelerationRate="fast"
      pinchGestureEnabled={false}
      automaticallyAdjustContentInsets={false}
      automaticallyAdjustKeyboardInsets={false}
      contentInsetAdjustmentBehavior="never"
      onScrollEndDrag={onScrollEnd}
      onMomentumScrollEnd={onMomentumScrollEnd}
      onContentSizeChange={onContentSizeChange}
      {...rest}>
      {slides.map(({key, props: daProps}) => (
        <View key={key} dataSet={slideDataSet} style={layoutStyles.slide}>
          <SlideComponent {...daProps} />
        </View>
      ))}
    </ScrollView>
  );
});

const layoutStyles = StyleSheet.create({
  contentContainerStyle: {
    flexDirection: 'row',
  },
  slide: {
    flex: 1,
  },
});

const carouselDataSet = {
  'scroll-snap-type': 'x mandatory',
};

const slideDataSet = {
  'scroll-snap-align': 'start',
  'scroll-snap-stop': 'always',
};

function lightImpactIfPossible() {
  lightImpact().catch(() => {});
}
